import { CheckCircleIcon, CloudArrowUpIcon } from '@phosphor-icons/react';
import { Upload as AntdUpload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { useRef, useState } from 'react';

import { Button } from '@/components/common/button';
import type { UploadFn, UploadMessages, UploadVariant, UploadedFile } from '@/lib/upload';
import { UPLOAD_PRESETS, validateFile } from '@/lib/upload';

import { UploadItem, type UploadEntry } from './upload-item';
import styles from './upload.module.scss';

type AntdUploadRequest = { file: File & { uid: string } };

export type { UploadVariant } from '@/lib/upload';

export type UploadProps = {
  variant: UploadVariant;
  onUpload: UploadFn;
  value?: UploadedFile[];
  onChange?: (files: UploadedFile[]) => void;
  maxCount?: number;
  maxSizeMb?: number;
  accept?: string;
  disabled?: boolean;
  /** Overrides for this zone's copy. Unset keys fall back to the variant default. */
  messages?: Partial<UploadMessages>;
  status?: 'error';
  id?: string;
};

type Entry = UploadEntry & { raw?: File; abort?: () => void; committed?: UploadedFile };

/** Copy used when a call site does not spread a preset: each variant borrows its own preset's. */
const DEFAULT_MESSAGES: Record<UploadVariant, UploadMessages> = {
  image: UPLOAD_PRESETS.componentImages.messages,
  document: UPLOAD_PRESETS.installationGuide.messages,
};

function makePreview(file: File): string | undefined {
  return typeof URL?.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined;
}

export function Upload({
  variant,
  onUpload,
  value,
  onChange,
  maxCount,
  maxSizeMb,
  accept,
  disabled,
  messages: messagesProp,
  status,
  id,
}: UploadProps) {
  const messages = { ...DEFAULT_MESSAGES[variant], ...messagesProp };
  const [entries, setEntriesState] = useState<Entry[]>(() =>
    (value ?? []).map((file) => ({
      uid: file.id,
      name: file.name,
      size: file.size,
      status: 'success' as const,
      percent: 100,
      url: file.url,
      committed: file,
    })),
  );
  const entriesRef = useRef(entries);
  const setEntries = (next: Entry[] | ((prev: Entry[]) => Entry[])) => {
    const resolved = typeof next === 'function' ? next(entriesRef.current) : next;
    entriesRef.current = resolved;
    setEntriesState(resolved);
  };

  const emit = (next: Entry[]) => {
    onChange?.(next.filter((e) => e.status === 'success' && e.committed).map((e) => e.committed!));
  };

  const patch = (uid: string, changes: Partial<Entry>, andEmit = false) => {
    if (andEmit) {
      const next = entriesRef.current.map((e) => (e.uid === uid ? { ...e, ...changes } : e));
      setEntries(next);
      emit(next);
    } else {
      setEntries((prev) => prev.map((e) => (e.uid === uid ? { ...e, ...changes } : e)));
    }
  };

  const runUpload = (file: File, uid: string) => {
    const controller = new AbortController();
    patch(uid, {
      status: 'uploading',
      percent: 0,
      error: undefined,
      abort: () => controller.abort(),
    });
    onUpload(file, {
      onProgress: (percent) => patch(uid, { percent }),
      signal: controller.signal,
    })
      .then((uploaded) =>
        patch(
          uid,
          {
            status: 'success',
            percent: 100,
            url: uploaded.url,
            name: uploaded.name,
            committed: uploaded,
          },
          true,
        ),
      )
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        patch(uid, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
      });
  };

  const handleRequest = (options: AntdUploadRequest) => {
    const file = options.file;
    setEntries((prev) => [
      ...prev,
      {
        uid: file.uid,
        name: file.name,
        size: file.size,
        status: 'uploading',
        percent: 0,
        raw: file,
        retriable: true,
        preview: variant === 'image' ? makePreview(file) : undefined,
      },
    ]);
    runUpload(file, file.uid);
  };

  const pushError = (file: RcFile, error: string) => {
    setEntries((prev) => [
      ...prev,
      { uid: file.uid, name: file.name, size: file.size, status: 'error', percent: 0, error },
    ]);
  };

  // A file `beforeUpload` refused: it carries a message but was never attached, so it holds no slot,
  // counts toward nothing, and renders as a message beside the zone rather than as a thumbnail. A
  // *transport* failure is the opposite case — `retriable`, still one of the Grower's five files,
  // and still a tile with a Retry on it. `pushError` never sets `retriable`; `handleRequest` always
  // does, and `runUpload`'s catch only patches `status`/`error`, so the flag survives the failure.
  const isRejected = (entry: Entry) => entry.status === 'error' && !entry.retriable;

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    const err = validateFile(file, { accept, maxSizeMb, messages });
    if (err) {
      pushError(file, err);
      return AntdUpload.LIST_IGNORE;
    }
    if (maxCount) {
      const batchIndex = fileList.indexOf(file);
      // Slots are held by everything the Grower picked that was not refused.
      const held = entriesRef.current.filter((e) => !isRejected(e)).length;
      if (held + batchIndex >= maxCount) {
        pushError(file, messages.tooMany);
        return AntdUpload.LIST_IGNORE;
      }
    }
    return true;
  };

  const handleRemove = (uid: string) => {
    const target = entriesRef.current.find((e) => e.uid === uid);
    target?.abort?.();
    if (target?.preview) URL.revokeObjectURL(target.preview);
    const next = entriesRef.current.filter((e) => e.uid !== uid);
    setEntries(next);
    emit(next);
  };

  const handleRetry = (uid: string) => {
    const target = entriesRef.current.find((e) => e.uid === uid);
    if (target?.raw) runUpload(target.raw, uid);
  };

  const rejected = entries.filter(isRejected);
  const attached = entries.filter((e) => !isRejected(e));
  const filledImageZone = variant === 'image' && attached.length > 0;

  return (
    <div className={styles.root} id={id}>
      {/* Stays enabled at `maxCount`: a disabled input swallows the attempt, and
          US-113319 requires the extra file to be refused with `messages.tooMany`. */}
      <AntdUpload.Dragger
        multiple
        accept={accept}
        showUploadList={false}
        disabled={disabled}
        beforeUpload={beforeUpload}
        customRequest={handleRequest as (options: object) => void}
        className={[styles.dragger, status === 'error' ? styles.error : undefined]
          .filter(Boolean)
          .join(' ')}
      >
        {filledImageZone ? (
          <div className={styles.filled}>
            <div className={styles.grid}>
              {attached.map((entry) => (
                <UploadItem
                  key={entry.uid}
                  variant="image"
                  entry={entry}
                  onRemove={handleRemove}
                  onRetry={handleRetry}
                />
              ))}
            </div>
            <div className={styles.more}>
              {/* No `stopPropagation` here, deliberately: the pill is the affordance Figma draws
                  for adding more images (430:405319), and the dragger's own click handler — an
                  ancestor React handler — is what opens the picker. Containing the click would
                  leave the button inert. A single bubbled click opens the picker exactly once. */}
              <Button variant="outline" size="small" startIcon={<CloudArrowUpIcon size={12} />}>
                {messages.uploadMore}
              </Button>
              <p className={styles.counter}>
                <CheckCircleIcon size={12} weight="fill" aria-hidden />
                {/* "...images *uploaded*": in-flight and failed files are not counted, so the number
                    always matches what the claim will actually carry. */}
                {`${attached.filter((e) => e.status === 'success').length} out of ${
                  maxCount ?? attached.length
                } images uploaded`}
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.empty}>
            <CloudArrowUpIcon size={32} aria-hidden />
            <p className={styles.help}>{messages.help}</p>
          </div>
        )}
      </AntdUpload.Dragger>
      {/* Refused files are messages, not thumbnails — ticket 01 asks for them "beside the upload
          zone". No Figma frame covers this state, so it reuses the row error's tokens. The document
          variant already surfaces its own errors in the list below, so it is left alone. */}
      {variant === 'image' && rejected.length > 0 ? (
        <div className={styles.list}>
          {rejected.map((entry) => (
            <div key={entry.uid} className={styles.rowError}>
              {entry.error}
            </div>
          ))}
        </div>
      ) : null}
      {variant === 'document' && entries.length > 0 ? (
        <div className={styles.list}>
          {entries.map((entry) => (
            <UploadItem
              key={entry.uid}
              variant="document"
              entry={entry}
              onRemove={handleRemove}
              onRetry={handleRetry}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
